import "@/index.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ChatNotifyProvider } from "@/context/ChatNotifyContext";
import { SpaceProvider } from "@/context/SpaceContext";
import { Toaster } from "@/components/ui/sonner";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Vault from "@/pages/Vault";
import CompanyMap from "@/pages/CompanyMap";
import ItemDetail from "@/pages/ItemDetail";
import Settings from "@/pages/Settings";
import Storage from "@/pages/Storage";
import Chat from "@/pages/Chat";
import ChangePassword from "@/pages/ChangePassword";
import SetupPasskey from "@/pages/SetupPasskey";
import AdminUsers from "@/pages/AdminUsers";
import Directory from "@/pages/Directory";
import Layout from "@/components/Layout";

function Loading() {
  return <div className="h-screen flex items-center justify-center text-muted-foreground">Loading vault…</div>;
}

function RequireAuth({ children }) {
  const { user, ready } = useAuth();
  if (!ready || user === null) return <Loading />;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function ProtectedRoute({ children, adminOnly }) {
  const { user, ready } = useAuth();
  if (!ready || user === null) return <Loading />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.must_change_password) return <Navigate to="/change-password" replace />;
  if (user.enforce_passkey_enrollment && !user.has_passkey) return <Navigate to="/setup-passkey" replace />;
  if (adminOnly && user.role !== "admin") return <Navigate to="/" replace />;
  return <Layout>{children}</Layout>;
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <ChatNotifyProvider>
            <SpaceProvider>
              <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/change-password" element={<RequireAuth><ChangePassword /></RequireAuth>} />
              <Route path="/setup-passkey" element={<RequireAuth><SetupPasskey /></RequireAuth>} />
              <Route path="/" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
              <Route path="/map" element={<ProtectedRoute><CompanyMap /></ProtectedRoute>} />
              <Route path="/directory" element={<ProtectedRoute><Directory /></ProtectedRoute>} />
              <Route path="/chat" element={<ProtectedRoute><Chat /></ProtectedRoute>} />
              <Route path="/vault/:vault" element={<ProtectedRoute><Vault /></ProtectedRoute>} />
              <Route path="/item/:id" element={<ProtectedRoute><ItemDetail /></ProtectedRoute>} />
              <Route path="/storage" element={<ProtectedRoute><Storage /></ProtectedRoute>} />
              <Route path="/admin/users" element={<ProtectedRoute adminOnly><AdminUsers /></ProtectedRoute>} />
              <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
            </SpaceProvider>
          </ChatNotifyProvider>
          <Toaster position="bottom-right" richColors />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
